import { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ProfileNode {
  id: string
  name: string
  type: 'profile'
  status: 'active' | 'warning' | 'error' | 'idle' | 'suspended'
  avatar?: string
  pagesCount?: number
}

export interface PageNode {
  id: string
  name: string
  type: 'page'
  profileId: string
  likes?: number
}

export interface MediaNode {
  id: string
  name: string
  type: 'media'
  pageId: string
  mediaType?: 'post' | 'reel' | 'story'
  engagement?: number
}

export type GraphNode = ProfileNode | PageNode | MediaNode

export interface Connection {
  id: string
  sourceId: string
  targetId: string
  type: 'profile-page' | 'page-media'
  strength?: number
}

interface NeuralGraphProps {
  profiles: ProfileNode[]
  pages: PageNode[]
  media: MediaNode[]
  connections: Connection[]
  selectedProfileId?: string | null
  bulkSelectedPageIds?: Set<string>
  onNodeClick?: (node: GraphNode) => void
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PROFILE_RADIUS = 26
const PAGE_RADIUS = 7
const MEDIA_SIZE = 18
const PAD_TOP = 60
const PAD_BOTTOM = 50

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  suspended: '#dc2626',
  idle: '#71717a',
}

const STATUS_BORDER: Record<string, string> = {
  active: '#22c55e50',
  warning: '#ca8a0450',
  error: '#dc262650',
  suspended: '#dc262680',
  idle: '#27272a',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'online',
  warning: 'aviso',
  error: 'erro',
  suspended: 'SUSPENSA',
  idle: 'idle',
}

const MIN_PROFILE_SPACING = 80
const MIN_PAGE_SPACING = 24
const MIN_MEDIA_SPACING = 50

/* ------------------------------------------------------------------ */
/*  Layout — distribute nodes top-to-bottom like the mockup            */
/* ------------------------------------------------------------------ */

function layoutNodes(
  profiles: ProfileNode[],
  pages: PageNode[],
  media: MediaNode[],
  w: number,
  h: number,
) {
  const positions: Record<string, { x: number; y: number }> = {}
  const cw = Math.max(w, 600)
  const ch = Math.max(h, 400)

  // Column X positions (mockup: 12%, 48%, 83%)
  const colX = { profile: cw * 0.12, page: cw * 0.48, media: cw * 0.83 }
  const top = PAD_TOP
  const bot = ch - PAD_BOTTOM

  const spread = (items: { id: string }[], x: number) => {
    if (items.length === 0) return
    if (items.length === 1) {
      positions[items[0].id] = { x, y: (top + bot) / 2 }
      return
    }
    const step = (bot - top) / (items.length - 1)
    items.forEach((item, i) => {
      positions[item.id] = { x, y: top + step * i }
    })
  }

  spread(profiles, colX.profile)
  spread(pages, colX.page)
  spread(media, colX.media)

  return positions
}

function getCanvasHeight(profileCount: number, pageCount: number, mediaCount: number, containerH: number): number {
  const profileH = profileCount > 1 ? (profileCount - 1) * MIN_PROFILE_SPACING + PAD_TOP + PAD_BOTTOM : 0
  const pageH = pageCount > 1 ? (pageCount - 1) * MIN_PAGE_SPACING + PAD_TOP + PAD_BOTTOM : 0
  const mediaH = mediaCount > 1 ? (mediaCount - 1) * MIN_MEDIA_SPACING + PAD_TOP + PAD_BOTTOM : 0
  return Math.max(containerH, profileH, pageH, mediaH)
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

/* ------------------------------------------------------------------ */
/*  Bezier — S-shaped curves matching mockup                           */
/* ------------------------------------------------------------------ */

function bezierPath(x1: number, y1: number, x2: number, y2: number): string {
  const dx = x2 - x1
  const cx1 = x1 + dx * 0.35
  const cy1 = y1
  const cx2 = x1 + dx * 0.65
  const cy2 = y2
  return `M${x1},${y1} C${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`
}

function hexagonPoints(cx: number, cy: number, size: number): string {
  const pts: string[] = []
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2
    pts.push(`${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`)
  }
  return pts.join(' ')
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function NeuralGraph({
  profiles,
  pages,
  media,
  connections,
  selectedProfileId,
  bulkSelectedPageIds,
  onNodeClick,
}: NeuralGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [didDrag, setDidDrag] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [mouseDownPos, setMouseDownPos] = useState({ x: 0, y: 0 })
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  const [containerSize, setContainerSize] = useState({ w: 1000, h: 700 })

  // Track container size
  useEffect(() => {
    if (!containerRef.current) return
    const obs = new ResizeObserver(([entry]) => {
      setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    obs.observe(containerRef.current)
    return () => obs.disconnect()
  }, [])

  const canvasHeight = useMemo(
    () => getCanvasHeight(profiles.length, pages.length, media.length, containerSize.h),
    [profiles.length, pages.length, media.length, containerSize.h],
  )

  const selectedProfilePageIds = useMemo(() => {
    if (!selectedProfileId) return new Set<string>()
    return new Set(
      connections
        .filter(c => c.type === 'profile-page' && c.sourceId === selectedProfileId)
        .map(c => c.targetId),
    )
  }, [selectedProfileId, connections])

  const positions = useMemo(
    () => layoutNodes(profiles, pages, media, containerSize.w, canvasHeight),
    [profiles, pages, media, containerSize.w, canvasHeight],
  )

  // Zoom toward cursor
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.08 : 0.08
    const newZoom = Math.max(0.15, Math.min(3, zoom + delta))
    const rect = containerRef.current?.getBoundingClientRect()
    if (rect) {
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      setPan((p) => ({
        x: mx - (mx - p.x) * (newZoom / zoom),
        y: my - (my - p.y) * (newZoom / zoom),
      }))
    }
    setZoom(newZoom)
  }, [zoom])

  // Pan — always start on mousedown, distinguish drag vs click on mouseup
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return
    setDragging(true)
    setDidDrag(false)
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    setMouseDownPos({ x: e.clientX, y: e.clientY })
  }, [pan])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY })
    if (dragging) {
      const dx = e.clientX - mouseDownPos.x
      const dy = e.clientY - mouseDownPos.y
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        setDidDrag(true)
      }
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y })
    }
  }, [dragging, dragStart, mouseDownPos])

  const handleMouseUp = useCallback(() => {
    setDragging(false)
  }, [])

  // Only fire node click if user didn't drag
  const handleNodeClickSafe = useCallback((node: GraphNode) => {
    if (!didDrag) onNodeClick?.(node)
  }, [didDrag, onNodeClick])

  const fitView = useCallback(() => {
    if (canvasHeight > containerSize.h) {
      const newZoom = Math.max(0.15, (containerSize.h / canvasHeight) * 0.92)
      setZoom(newZoom)
    } else {
      setZoom(1)
    }
    setPan({ x: 0, y: 0 })
  }, [canvasHeight, containerSize.h])

  // Only auto-fit on initial data load (0→N), not on subsequent changes
  const prevNodeCount = useRef(0)
  useEffect(() => {
    const total = profiles.length + pages.length + media.length
    if (prevNodeCount.current === 0 && total > 0) {
      fitView()
    }
    prevNodeCount.current = total
  }, [profiles.length, pages.length, media.length, fitView])

  const zoomIn = () => setZoom((z) => Math.min(3, z + 0.15))
  const zoomOut = () => setZoom((z) => Math.max(0.15, z - 0.15))

  const allNodes: GraphNode[] = useMemo(
    () => [...profiles, ...pages, ...media],
    [profiles, pages, media],
  )

  // Stable random seeds for particle durations
  const particleSeeds = useMemo(() => {
    return connections.map((_, i) => ({
      dur1: 3 + ((i * 7 + 13) % 40) / 10,
      dur2: 4 + ((i * 11 + 7) % 30) / 10,
      delay1: ((i * 3 + 5) % 30) / 10,
      delay2: ((i * 5 + 17) % 25) / 10,
    }))
  }, [connections])

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden select-none"
      style={{ cursor: dragging ? 'grabbing' : 'grab' }}
    >
      <svg
        ref={svgRef}
        className="w-full h-full"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Background */}
        <rect className="graph-bg" width="100%" height="100%" fill="#09090b" />

        <defs>
          {/* Dot grid pattern (mockup style) */}
          <pattern id="dotgrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="20" cy="20" r="0.8" fill="#18181b" />
          </pattern>
          {/* Glow filters */}
          <filter id="glow-indigo" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor="#6366f1" floodOpacity="0.3" />
            <feComposite in2="blur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor="#22c55e" floodOpacity="0.3" />
            <feComposite in2="blur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width="100%" height="100%" fill="url(#dotgrid)" opacity="0.4" />

        {/* Transform group for zoom+pan */}
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Column labels */}
          {profiles.length > 0 && (
            <text
              x={containerSize.w * 0.12}
              y={24}
              textAnchor="middle"
              fill="#52525b"
              fontSize={10}
              fontWeight={600}
              fontFamily="Inter, system-ui, sans-serif"
              letterSpacing="2px"
              style={{ pointerEvents: 'none', textTransform: 'uppercase' } as React.CSSProperties}
            >
              PERFIS
            </text>
          )}
          {pages.length > 0 && (
            <text
              x={containerSize.w * 0.48}
              y={24}
              textAnchor="middle"
              fill="#52525b"
              fontSize={10}
              fontWeight={600}
              fontFamily="Inter, system-ui, sans-serif"
              letterSpacing="2px"
              style={{ pointerEvents: 'none' }}
            >
              PÁGINAS
            </text>
          )}
          {media.length > 0 && (
            <text
              x={containerSize.w * 0.83}
              y={24}
              textAnchor="middle"
              fill="#52525b"
              fontSize={10}
              fontWeight={600}
              fontFamily="Inter, system-ui, sans-serif"
              letterSpacing="2px"
              style={{ pointerEvents: 'none' }}
            >
              MÍDIA
            </text>
          )}

          {/* ── Connections ── */}
          {connections.map((conn, i) => {
            const src = positions[conn.sourceId]
            const tgt = positions[conn.targetId]
            if (!src || !tgt) return null

            const isProfilePage = conn.type === 'profile-page'
            const isActiveConn = !selectedProfileId || conn.sourceId === selectedProfileId
            const strokeColor = isProfilePage ? '#3f3f46' : '#2d4a35'
            const particleColor = isProfilePage ? '#6366f1' : '#22c55e'
            const particleColor2 = isProfilePage ? '#818cf8' : '#4ade80'
            const pathId = `path-${conn.id}`
            const d = bezierPath(src.x, src.y, tgt.x, tgt.y)
            const seed = particleSeeds[i] || { dur1: 4, dur2: 5, delay1: 0, delay2: 2 }

            return (
              <g key={conn.id} opacity={isActiveConn ? 1 : 0.08}>
                <path
                  id={pathId}
                  d={d}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={1}
                />
                {/* Particle 1 — with fade in/out */}
                <circle r={1.5} fill={particleColor}>
                  <animateMotion
                    dur={`${seed.dur1}s`}
                    begin={`${seed.delay1}s`}
                    repeatCount="indefinite"
                  >
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0.7;0.7;0"
                    dur={`${seed.dur1}s`}
                    begin={`${seed.delay1}s`}
                    repeatCount="indefinite"
                  />
                </circle>
                {/* Particle 2 — offset, smaller */}
                <circle r={1} fill={particleColor2}>
                  <animateMotion
                    dur={`${seed.dur2}s`}
                    begin={`${seed.delay2}s`}
                    repeatCount="indefinite"
                  >
                    <mpath href={`#${pathId}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0.5;0.5;0"
                    dur={`${seed.dur2}s`}
                    begin={`${seed.delay2}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            )
          })}

          {/* ── Profile nodes ── */}
          {profiles.map((profile) => {
            const pos = positions[profile.id]
            if (!pos) return null
            const statusColor = STATUS_COLORS[profile.status] || STATUS_COLORS.idle
            const borderColor = STATUS_BORDER[profile.status] || STATUS_BORDER.idle
            const isHovered = hoveredNode?.id === profile.id
            const isSelected = profile.id === selectedProfileId

            return (
              <g
                key={profile.id}
                className="cursor-pointer"
                onMouseUp={() => handleNodeClickSafe(profile)}
                onMouseEnter={() => setHoveredNode(profile)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                {/* Rotating dashed arc */}
                {profile.status === 'active' && (
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={PROFILE_RADIUS + 6}
                    fill="none"
                    stroke="#16653440"
                    strokeWidth={1}
                    strokeDasharray="4 6"
                  >
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from={`0 ${pos.x} ${pos.y}`}
                      to={`360 ${pos.x} ${pos.y}`}
                      dur="8s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
                {/* Selected ring */}
                {isSelected && (
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={PROFILE_RADIUS + 5}
                    fill="none"
                    stroke="#6366f140"
                    strokeWidth={2}
                  />
                )}
                {/* Main circle */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={PROFILE_RADIUS}
                  fill="#111113"
                  stroke={isSelected ? '#6366f1' : borderColor}
                  strokeWidth={isSelected ? 2 : 1.5}
                  filter={isHovered || isSelected ? 'url(#glow-indigo)' : undefined}
                />
                {/* Two-letter initials */}
                <text
                  x={pos.x}
                  y={pos.y + 1}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#71717a"
                  fontSize={14}
                  fontWeight={600}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {getInitials(profile.name)}
                </text>
                {/* Status dot */}
                <circle
                  cx={pos.x + PROFILE_RADIUS * 0.7}
                  cy={pos.y - PROFILE_RADIUS * 0.7}
                  r={4}
                  fill={statusColor}
                  stroke="#111113"
                  strokeWidth={2}
                />
                {/* Name below */}
                <text
                  x={pos.x}
                  y={pos.y + PROFILE_RADIUS + 14}
                  textAnchor="middle"
                  fill="#d4d4d8"
                  fontSize={11}
                  fontWeight={500}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {profile.name}
                </text>
                {/* Status label */}
                <text
                  x={pos.x}
                  y={pos.y + PROFILE_RADIUS + 28}
                  textAnchor="middle"
                  fill={statusColor}
                  fontSize={9}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {STATUS_LABELS[profile.status] || 'idle'}
                </text>
              </g>
            )
          })}

          {/* ── Page nodes (mockup: label to the right) ── */}
          {pages.map((page) => {
            const pos = positions[page.id]
            if (!pos) return null
            const isHovered = hoveredNode?.id === page.id
            const isHighlighted = !!selectedProfileId && selectedProfilePageIds.has(page.id)
            const isBulkSelected = bulkSelectedPageIds?.has(page.id) ?? false
            const isDimmed = !!selectedProfileId && !isHighlighted

            const enlarged = isBulkSelected || isHighlighted
            const r = enlarged ? PAGE_RADIUS + 2 : PAGE_RADIUS
            const fill = isBulkSelected ? '#2d1215' : isHighlighted ? '#1e1b4b' : '#18181b'
            const stroke = isBulkSelected ? '#ef4444' : isHighlighted ? '#6366f1' : isHovered ? '#6366f1' : '#27272a'
            const sw = enlarged ? 1.5 : 1
            const dotFill = isBulkSelected ? '#f87171' : isHighlighted ? '#818cf8' : '#71717a'
            const labelFill = isBulkSelected ? '#fca5a5' : isHighlighted ? '#c7d2fe' : isHovered ? '#d4d4d8' : '#a1a1aa'

            return (
              <g
                key={page.id}
                className="cursor-pointer"
                onMouseUp={() => handleNodeClickSafe(page)}
                onMouseEnter={() => setHoveredNode(page)}
                onMouseLeave={() => setHoveredNode(null)}
                opacity={isDimmed ? 0.12 : 1}
              >
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={r}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={sw}
                  filter={isHighlighted ? 'url(#glow-indigo)' : undefined}
                />
                {/* Inner dot */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={enlarged ? 3 : 2}
                  fill={dotFill}
                />
                {/* Label to the right (mockup style) */}
                <text
                  x={pos.x + r + 8}
                  y={pos.y + 1}
                  textAnchor="start"
                  dominantBaseline="central"
                  fill={labelFill}
                  fontSize={enlarged ? 11 : 10}
                  fontWeight={enlarged ? 600 : 400}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {page.name}
                </text>
              </g>
            )
          })}

          {/* ── Media nodes (hexagons) ── */}
          {media.map((m) => {
            const pos = positions[m.id]
            if (!pos) return null
            const isHovered = hoveredNode?.id === m.id

            return (
              <g
                key={m.id}
                className="cursor-pointer"
                onMouseUp={() => handleNodeClickSafe(m)}
                onMouseEnter={() => setHoveredNode(m)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                <polygon
                  points={hexagonPoints(pos.x, pos.y, MEDIA_SIZE)}
                  fill="#0f1f14"
                  stroke={isHovered ? '#22c55e' : '#22c55e30'}
                  strokeWidth={1}
                  filter={isHovered ? 'url(#glow-green)' : undefined}
                />
                {/* Type indicator */}
                <text
                  x={pos.x}
                  y={pos.y + 1}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#22c55e80"
                  fontSize={10}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {m.mediaType === 'reel' ? 'R' : m.mediaType === 'story' ? 'S' : 'F'}
                </text>
                {/* Name below */}
                <text
                  x={pos.x}
                  y={pos.y + MEDIA_SIZE + 12}
                  textAnchor="middle"
                  fill="#4ade80"
                  fontSize={10}
                  fontFamily="Inter, system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {m.name}
                </text>
              </g>
            )
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {hoveredNode && (
        <div
          className="fixed z-50 rounded-lg bg-[#18181b] border border-[#27272a] px-3 py-2 shadow-xl pointer-events-none"
          style={{
            left: mousePos.x + 16,
            top: mousePos.y - 10,
          }}
        >
          <div className="text-xs font-semibold text-zinc-200">{hoveredNode.name}</div>
          <div className="text-[10px] text-zinc-500 capitalize mt-0.5">
            {hoveredNode.type === 'profile' && `Perfil — ${STATUS_LABELS[(hoveredNode as ProfileNode).status] || 'idle'}`}
            {hoveredNode.type === 'page' && `Página — ${(hoveredNode as PageNode).likes ?? 0} curtidas`}
            {hoveredNode.type === 'media' && `Mídia — ${(hoveredNode as MediaNode).mediaType ?? 'post'}`}
          </div>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-6 right-6 z-20 flex flex-col gap-1.5">
        <button
          onClick={zoomIn}
          className="flex h-8 w-8 items-center justify-center rounded bg-[#0c0c10]/90 border border-[#27272a] text-zinc-500 hover:text-zinc-300 hover:border-[#3f3f46] transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={zoomOut}
          className="flex h-8 w-8 items-center justify-center rounded bg-[#0c0c10]/90 border border-[#27272a] text-zinc-500 hover:text-zinc-300 hover:border-[#3f3f46] transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={fitView}
          className="flex h-8 w-8 items-center justify-center rounded bg-[#0c0c10]/90 border border-[#27272a] text-zinc-500 hover:text-zinc-300 hover:border-[#3f3f46] transition-colors"
          title="Ajustar ao quadro"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <div className="text-center text-[9px] text-zinc-600 font-mono mt-1">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Empty state */}
      {allNodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#18181b] border border-[#27272a]">
              <svg className="h-8 w-8 text-zinc-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="6" cy="6" r="3" />
                <circle cx="18" cy="18" r="3" />
                <circle cx="18" cy="6" r="3" />
                <path d="M8.5 7.5L15.5 16.5" />
                <path d="M8.5 6L15.5 6" />
              </svg>
            </div>
            <p className="text-sm text-zinc-500 font-medium">Nenhum perfil adicionado</p>
            <p className="text-xs text-zinc-600 mt-1">Adicione um perfil para visualizar o grafo</p>
          </div>
        </div>
      )}
    </div>
  )
}
